import { DynamoContextProvider } from "@caprikorn/dynamo-ui";
import { datadogRum } from "@datadog/browser-rum";
import flagsmith from "@flagsmith/flagsmith";
import { FlagsmithProvider } from "@flagsmith/flagsmith/react";
import { QueryClientProvider } from "@tanstack/react-query";
import { type ReactElement, useEffect } from "react";

import { CobrowseIntegration } from "@components/cobrowse/cobrowse";
import { TenantProvider } from "@contexts/Tenant/Tenant.context";
import { ThemeProvider } from "@contexts/Theme/Theme.context";
import { LS_KEYS, THEME_VERSION } from "@core/core.constants";
import { Router } from "@router/Router";
import { GROUP_REFERENCE } from "@services/http";
import { GROUP_ID_LENGTH } from "@services/services.constants";
import { TranslationPanel } from "@utils/translations/TranslationPanel/TranslationPanel";
import { useCustomTranslation } from "@utils/translations/useCustomTranslation";

import { queryClient } from "./AppQueryClient";

import "./datadog.conf";

export const App = (): ReactElement => {
  const { i18n } = useCustomTranslation("common");
  useEffect(() => {
    flagsmith.startListening(import.meta.env.VITE_APP_FLAGSMITH_TTL);
    return flagsmith.stopListening();
  }, []);

  // only to ensure group is correctly set on header when F5.
  const currentGroup = localStorage.getItem(LS_KEYS.CURRENT_GROUP_REF) || "";

  if (currentGroup.length === GROUP_ID_LENGTH) {
    // TODO fix this crappy pattern
    // eslint-disable-next-line react-hooks/immutability
    GROUP_REFERENCE.current = currentGroup;
  }

  return (
    <DynamoContextProvider
      language={i18n.language as "fr" | "en"}
      tenant={
        window.tenant === "kuriosity"
          ? localStorage.getItem(LS_KEYS.THEME_NAME) || window.tenant
          : window.tenant
      }
      themeVersion={THEME_VERSION}
    >
      <FlagsmithProvider
        flagsmith={flagsmith}
        options={{
          environmentID: import.meta.env.VITE_APP_FLAGSMITH_KEY || "",
          datadogRum: { client: datadogRum, trackTraits: false },
          cacheFlags: true,
          cacheOptions: {
            ttl: import.meta.env.VITE_APP_FLAGSMITH_TTL,
            skipAPI: true,
            loadStale: false,
          },
        }}
      >
        <QueryClientProvider client={queryClient}>
          <ThemeProvider>
            <TenantProvider>
              <CobrowseIntegration />
              <Router />
              {import.meta.env.VITE_APP_EDIT_TRANSLATION === "true" && (
                <TranslationPanel />
              )}
            </TenantProvider>
          </ThemeProvider>
        </QueryClientProvider>
      </FlagsmithProvider>
    </DynamoContextProvider>
  );
};
